import {
  getData,
  updateData,
  removeData
} from './api-wrapper.service';
import { database, auth } from './core';
import { ref, push } from 'firebase/database';
import logger from '../../utils/logger';

/**
 * Flow service matching original app's flows.service.js
 */

// Technique reference structure that matches original app
export interface TechniqueReference {
  key?: string; // Firebase key - matches original app structure
  name: string;
  sortname: string;
  type: 'position' | 'technique';
  isTrainingWikiTech?: boolean;
  isDefault?: boolean;
  user?: string; // User UID if personal technique
}

// Flow data structure matching original app exactly
export interface FlowData {
  key?: string;
  rawText: string; // The full text with technique references (e.g., "Started with #armbar then transitioned to #triangle")
  referencedTechs: Record<string, TechniqueReference>; // Map of technique keys to technique data
}

/**
 * Add a new flow
 * Matches original app: flows.service.js - addNewFlow
 * Original implementation: const addNewFlow = flow => resourceRef().push(flow);
 */
export const addNewFlow = async (flow: FlowData): Promise<string> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('No authenticated user');
    }

    // Match original app exactly: resourceRef().push(flow)
    // Single push() call that both generates key AND writes data
    const basePath = `flows/${currentUser.uid}`;
    const flowRef = ref(database, basePath);
    const newFlowRef = await push(flowRef, flow);

    logger.info(`Flow saved to path: ${basePath}/${newFlowRef.key}`);
    return newFlowRef.key || '';
  } catch (error: any) {
    logger.error('Error adding flow:', error?.message || error);
    throw error;
  }
};

/**
 * Update an existing flow
 * Note: Original app only has addNewFlow, no update method
 */
export const updateFlow = async (flowKey: string, updatedData: FlowData): Promise<void> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('No authenticated user');
    }

    // Update the entire flow data
    await updateData(`flows/${currentUser.uid}/${flowKey}`, updatedData);
  } catch (error) {
    logger.error('Error updating flow:', error);
    throw error;
  }
};

/**
 * Remove a flow
 */
export const removeFlow = async (flowKey: string): Promise<void> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('No authenticated user');
    }

    await removeData(`flows/${currentUser.uid}/${flowKey}`);
  } catch (error) {
    logger.error('Error removing flow:', error);
    throw error;
  }
};

/**
 * Get all flows for the current user
 */
export const getUserFlows = async (): Promise<Record<string, FlowData>> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('No authenticated user');
    }

    const flows = await getData(`flows/${currentUser.uid}`);
    return flows || {};
  } catch (error) {
    logger.error('Error fetching user flows:', error);
    throw error;
  }
};

/**
 * Get a single flow
 */
export const getFlow = async (flowKey: string): Promise<FlowData | null> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('No authenticated user');
    }

    const flow = await getData(`flows/${currentUser.uid}/${flowKey}`);
    if (flow) {
      return {
        ...flow,
        key: flowKey
      };
    }
    return null;
  } catch (error) {
    logger.error('Error fetching flow:', error);
    throw error;
  }
};